import type { ProductTileProps } from '@Domains/Product/Tiles/types'
import { ProductListBlockFacetValueFragmentFragment, ProductListBlockFragmentFragment, SinglePostQuery, WordbookFragmentFragment } from '@Generated/graphql'
import { Group } from '@Globals/SplitTests/types'

export type AlgoliaProductTile = ProductTileProps & {
  category?: string
  groupID: string | null
  priceValue?: number | string
}

export type AlgoliaProductV2 = {
  assets?: {
    dimensions: {
      height: number
      width: number
    }
    imageToEdge?: boolean
    url: string
  }[]
  assortmentLadder?: {
    asset?: {
      dimensions: {
        height: number
        width: number
      }
      url: string
    }
    tag: string
  }
  baseDisplayPrice: {
    currencyCode: string
    format: string
    plain: string
    value: number
  }
  basePrice: number
  brand: string
  brandFacet: string
  bundle?: {
    children: {
      productID: string
      quantity: number
    }[]
    combinedValue: {
      countryCode: string
      currencyCode: string
      format: string
      plain: string
      value: number
    }
  }
  categories: string
  categoryIDs: string[]
  categoryMain: string
  categoryPath: string
  categorySub: string
  createdAt: number
  displayPrice: {
    currencyCode: string
    discount?: {
      code: {
        context?: string
        id: string
      }
      format: string
      percent: number
      plain: string
      value: number
    }
    format: string
    plain: string
    value: number
  }
  groupID: string
  hoverAsset: {
    dimensions: {
      height: number
      width: number
    }
    imageToEdge?: boolean
    url: string
  }
  isBundleOffer: boolean
  isEnabled: boolean
  isInStock: boolean
  isNew: boolean
  isPublished: boolean
  objectID: string
  personalizationTypes: string[]
  price: number
  primaryAsset: {
    dimensions: {
      height: number
      width: number
    }
    imageToEdge?: boolean
    url: string
  }
  productType: string
  slug: string
  title: string
  variantsColorCount: number
} & Record<string, any>

export enum ProductSorting {
  Default = 'default',
  Latest = 'latest',
  PriceAscending = 'price_asc',
  PriceDescending = 'price_desc',
}

export interface NumericFilter {
  max: number
  min: number
}

export interface Options {
  facetFilters: string[]
  facets: string[]
  filters: string
  hitsPerPage?: string
  locale: string
  numericFilters: Record<string, NumericFilter>
  optionalFilters: string[]
  page: number | null
  pagination: boolean
  query?: string
  sorting: ProductSorting
  sortingFeatureGroup: Group
  sortingGroup: Group
}

export type FacetStats = Record<string, {
  avg: number
  max: number
  min: number
  sum: number
}>

export interface Result {
  facets: Record<string, Record<string, number>>
  facetsStats: FacetStats
  hits: AlgoliaProductV2[]
  objectIDs: string[]
  page: number
  pages: number
  totalHits: number
}

export type FacetConfigGrouped<
  T extends { type: string, valuesObject: any, valuesObjectSorted: string[] },
  Type extends string,
> = Omit<T, 'type' | 'valuesObject' | 'valuesObjectSorted'> & {
  groupsObject: Record<string, {
    key: string
    name: string
    show?: boolean
    translationKey?: string
    valuesObject: T['valuesObject']
    valuesObjectSorted: T['valuesObjectSorted']
  }>
  groupsObjectSorted: string[]
  showGroupTitle?: boolean
  translationKey?: string
  type: Type
}

export type FacetConfig = NonNullable<ProductListBlockFragmentFragment['facetConfig']>[0]
export type FacetConfigGroup = NonNullable<FacetConfig['groups']>[0]
export type FacetConfigValue = ProductListBlockFacetValueFragmentFragment

export type FacetDynamicConfigFacet = {
  count: number
  disabled: boolean
  name: WordbookFragmentFragment | null
  showGroupTitle: boolean
  type: 'facet'
}

export type FacetDynamicConfigValue = {
  checked: boolean
  count: number
  disabled: boolean
  key: string
  name: WordbookFragmentFragment | null
  type: 'value'
}

export type FacetDynamicConfig = FacetDynamicConfigFacet | FacetDynamicConfigValue

export interface BlockConfigAlgolia {
  canonicals: Record<string, { name: WordbookFragmentFragment }>
  hits: Record<string, AlgoliaProductTile>
  hitsArray: AlgoliaProductTile[]
  initialFacetFilters: string[]
  initialFacetStats: FacetStats
  initialNumericFilters: Record<string, NumericFilter>
  initialSorting: ProductSorting
  optionalFilters: string[]
  page: number | null
  totalHits: number
  usePagination: boolean
  useQueryParams: boolean
}

type PageType = NonNullable<SinglePostQuery['page']>
type PageBlock = NonNullable<PageType['blocks']>[0]
export type PageProductListBlock = Extract<PageBlock, { __typename: 'ProductListBlock' }>
export type PageProductListBlockGuidedFilter = NonNullable<PageProductListBlock['guidedFilters']>[0]
export type PageProductListBlockInjection = NonNullable<PageProductListBlock['injections']>[0]
